import React from "react";
import {
  User,
  Settings,
  CreditCard,
  LogOut,
  AlertTriangle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ThemeToggle } from "./ThemeToggle";
import { formatWalletAddress } from "../../utils/wallet";

interface ProfileMenuProps {
  showUserMenu: boolean;
  setShowUserMenu: (show: boolean) => void;
  onSignOut: () => void;
  displayName: string;
  credits: number;
  theme: "light" | "dark" | "system";
  toggleTheme: () => void;
  getThemeIcon: () => React.ComponentType;
  getThemeText: () => string;
  onPurchaseCredits: () => void;
  walletAddress: string;
  nfdName?: string | null;
  balance: number;
  minBalance: number;
  rekeyTo?: string | null;
  profile?: any;
}

const getAvatarUrl = (profile: any): string | null => {
  try {
    const avatarIpfs = profile?.nfd?.properties?.verified?.avatar;
    if (avatarIpfs) {
      return avatarIpfs.replace("ipfs://", "https://ipfs.io/ipfs/");
    }
    return null;
  } catch {
    return null;
  }
};

export const ProfileMenu: React.FC<ProfileMenuProps> = ({
  showUserMenu,
  setShowUserMenu,
  onSignOut,
  displayName,
  credits,
  theme,
  toggleTheme,
  getThemeIcon,
  getThemeText,
  onPurchaseCredits,
  walletAddress,
  nfdName,
  balance,
  minBalance,
  rekeyTo,
  profile,
}) => {
  const navigate = useNavigate();
  const avatarUrl = profile ? getAvatarUrl(profile) : null;
  const availableBalance = balance - minBalance;
  const formattedAddress = formatWalletAddress(walletAddress);

  const handleProfileClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowUserMenu(false);
    navigate("/profile");
  };

  const handleSettingsClick = () => {
    setShowUserMenu(false);
  };

  if (!showUserMenu) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50"
        onClick={() => setShowUserMenu(false)}
      />
      <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden bg-blue-600 dark:bg-blue-500">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    target.parentElement?.classList.add("fallback-icon");
                  }}
                />
              ) : (
                <User size={24} className="text-white" />
              )}
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {displayName}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                {formattedAddress}
              </div>
            </div>
          </div>
          {nfdName && (
            <a
              href={`https://${nfdName}.xyz`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 mt-1 inline-block transition-colors"
            >
              {nfdName}
            </a>
          )}
          <div className="mt-2 space-y-1">
            <div className="text-xs">
              <span className="text-gray-500 dark:text-gray-400">
                Balance:{" "}
              </span>
              <span className="text-gray-900 dark:text-white font-medium">
                {(balance / 1e6).toFixed(6)} ALGO
              </span>
            </div>
            <div className="text-xs">
              <span className="text-gray-500 dark:text-gray-400">
                Available:{" "}
              </span>
              <span className="text-gray-900 dark:text-white font-medium">
                {(availableBalance / 1e6).toFixed(6)} ALGO
              </span>
            </div>
            <div className="text-xs">
              <span className="text-gray-500 dark:text-gray-400">
                Credits:{" "}
              </span>
              <span className="text-gray-900 dark:text-white font-medium">
                {credits}
              </span>
            </div>
            {rekeyTo && (
              <div className="flex items-center space-x-1 text-amber-600 dark:text-amber-400">
                <AlertTriangle size={12} />
                <span className="text-xs">Rekeyed to another account</span>
              </div>
            )}
          </div>
        </div>
        <div className="py-1">
          <button
            onClick={handleProfileClick}
            className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <User size={16} className="mr-2" />
            User Profile
          </button>
          {/* <button
            onClick={handleSettingsClick}
            className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Settings size={16} className="mr-2" />
            Settings
          </button> */}
          <button
            onClick={onPurchaseCredits}
            className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <CreditCard size={16} className="mr-2" />
            Buy Credits
          </button>
          <ThemeToggle
            theme={theme}
            toggleTheme={toggleTheme}
            getThemeIcon={getThemeIcon}
            getThemeText={getThemeText}
          />
          <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
          <button
            onClick={() => {
              setShowUserMenu(false);
              onSignOut();
            }}
            className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <LogOut size={16} className="mr-2" />
            Sign out
          </button>
        </div>
      </div>
    </>
  );
};
